import { useState } from 'react';

import {
  type CreateDiResourceRequest,
  type DiResourceResponse,
  type UpdateDiResourceRequest,
} from '../api';
import { useLicensePlans } from '../hooks/useLicensePlans';

type FormMode = 'create' | 'edit';

interface DiResourceFormProps {
  mode: FormMode;
  resource?: DiResourceResponse;
  busy: boolean;
  onClose: () => void;
  onCreate?: (request: CreateDiResourceRequest) => void;
  onUpdate?: (request: UpdateDiResourceRequest) => void;
}

export function DiResourceForm({ mode, resource, busy, onClose, onCreate, onUpdate }: DiResourceFormProps): JSX.Element {
  const { data: plans } = useLicensePlans();
  const [displayName, setDisplayName] = useState(resource?.displayName ?? '');
  const [endpoint, setEndpoint] = useState(resource?.endpoint ?? '');
  const [apiKey, setApiKey] = useState('');
  const [assignedLicenses, setAssignedLicenses] = useState<string[]>(
    resource?.assignedLicenses ?? [],
  );

  const toggleLicense = (l: string): void => {
    setAssignedLicenses((prev) =>
      prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l],
    );
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (mode === 'create') {
      onCreate?.({ displayName, endpoint, apiKeyPlaintext: apiKey, assignedLicenses });
    } else {
      const request: UpdateDiResourceRequest = {};
      if (displayName !== resource?.displayName) request.displayName = displayName;
      if (endpoint !== resource?.endpoint) request.endpoint = endpoint;
      const changed =
        JSON.stringify([...assignedLicenses].sort()) !==
        JSON.stringify([...(resource?.assignedLicenses ?? [])].sort());
      if (changed) request.assignedLicenses = assignedLicenses;
      onUpdate?.(request);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="drf-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <h2 id="drf-title" className="text-lg font-semibold text-slate-900">
          {mode === 'create' ? 'Añadir recurso DI' : 'Editar recurso DI'}
        </h2>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label" htmlFor="drf-name">Nombre</label>
            <input id="drf-name" className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={busy} required />
          </div>
          <div>
            <label className="label" htmlFor="drf-endpoint">Endpoint Azure DI</label>
            <input id="drf-endpoint" className="input" placeholder="https://…" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} disabled={busy} required />
          </div>
          {mode === 'create' ? (
            <div>
              <label className="label" htmlFor="drf-key">API key</label>
              <input id="drf-key" type="password" autoComplete="new-password" className="input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} disabled={busy} required />
            </div>
          ) : null}
          <div>
            <p className="label mb-1">Planes asignados</p>
            <div className="flex flex-wrap gap-4">
              {(plans ?? []).map((p) => (
                <label key={p.licenseType} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={assignedLicenses.includes(p.licenseType)}
                    onChange={() => toggleLicense(p.licenseType)}
                    disabled={busy}
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  {p.displayName}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Guardando…' : mode === 'create' ? 'Crear' : 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
}
